import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import Swal from "sweetalert2";
import { catchError } from "rxjs/operators";
import { of } from "rxjs";

import {
  Competencia,
  CompetenciaPayload,
  CompetenciaService,
} from "../../../../../shared/services/competencia.service";
import { Utils } from "../../../../../shared/utils/Utils";

@Component({
  selector: 'app-competencia',
  templateUrl: './competencia.component.html',
  styleUrls: ['./competencia.component.scss']
})
export class CompetenciaComponent implements OnInit {

competenciaForm!: FormGroup;

  competencias: Competencia[] = [];
  competenciasFiltradas: Competencia[] = [];

  filtro = "";
  filtroEstado: number | null = null;
  pageSize = 10;
  page = 1;

  cargandoListado = false;
  guardando = false;

  // Edición
  modoEdicion = false;
  idEditando: number | null = null;

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private competenciaService: CompetenciaService,
  ) {}

  ngOnInit(): void {
    this.inicializarFormulario();
    this.cargarCompetencias();
  }

  private inicializarFormulario(): void {
    this.competenciaForm = this.fb.group({
      descripcion: ["", [Validators.required, Validators.maxLength(255)]],
    });
  }

  // 🔹 Listado
  cargarCompetencias(): void {
    this.cargandoListado = true;

    this.competenciaService
      .getAll()
      .pipe(catchError(() => of([])))
      .subscribe((response: any) => {
        this.competencias = this.normalizarLista(response);
        this.aplicarFiltro();
        this.cargandoListado = false;
      });
  }

  private normalizarLista(response: any): Competencia[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  }

  aplicarFiltro(): void {
    const termino = String(this.filtro || "")
      .toLowerCase()
      .trim();

    let filtradas = this.competencias.filter((item) => {
      const coincideTexto =
        !termino || String(item.descripcion || "").toLowerCase().includes(termino);

      const estadoItem = Number(item.estado);
      const coincideEstado =
        this.filtroEstado === null || estadoItem === this.filtroEstado;

      return coincideTexto && coincideEstado;
    });

    filtradas = filtradas.sort((a, b) => {
      const estadoA = Number(a.estado) === 1 ? 0 : 1;
      const estadoB = Number(b.estado) === 1 ? 0 : 1;
      if (estadoA !== estadoB) return estadoA - estadoB;
      return (a.id || 0) - (b.id || 0);
    });

    this.competenciasFiltradas = filtradas;
    this.page = 1;
  }

  // 🔹 Abrir modal para crear
  abrirModalCrear(modalRef: any): void {
    this.modoEdicion = false;
    this.idEditando = null;
    this.competenciaForm.reset();
    this.modalService.open(modalRef, { centered: true, size: "lg" });
  }

  // 🔹 Abrir modal para editar
  abrirModalEditar(modalRef: any, competencia: Competencia): void {
    this.modoEdicion = true;
    this.idEditando = Number(competencia.id);

    this.competenciaForm.patchValue({
      descripcion: competencia.descripcion,
    });

    this.modalService.open(modalRef, { centered: true, size: "lg" });
  }

  // 🔹 Guardar (crea o actualiza según modoEdicion)
  guardar(modal: any): void {
    if (this.competenciaForm.invalid) {
      this.competenciaForm.markAllAsTouched();
      Swal.fire(
        "Campos obligatorios",
        "Completa la descripción de la competencia.",
        "warning",
      );
      return;
    }

    const descripcion = String(
      this.competenciaForm.get("descripcion")?.value || "",
    )
      .trim()
      .toUpperCase();

    this.guardando = true;

    if (this.modoEdicion && this.idEditando) {
      const payload: Partial<CompetenciaPayload> = { descripcion };

      this.competenciaService.update(this.idEditando, payload).subscribe({
        next: () => {
          this.guardando = false;
          modal.close();
          Swal.fire("Éxito", "Competencia actualizada correctamente.", "success");
          this.cargarCompetencias();
        },
        error: (error: any) => {
          this.guardando = false;
          const mensaje =
            error?.error?.error || "No se pudo actualizar la competencia.";
          Swal.fire("Error", mensaje, "error");
        },
      });
    } else {
      const payload: CompetenciaPayload = { descripcion, estado: 1 };

      this.competenciaService.create(payload).subscribe({
        next: () => {
          this.guardando = false;
          modal.close();
          Swal.fire("Éxito", "Competencia creada correctamente.", "success");
          this.cargarCompetencias();
        },
        error: (error: any) => {
          this.guardando = false;
          const mensaje =
            error?.error?.error || "No se pudo crear la competencia.";
          Swal.fire("Error", mensaje, "error");
        },
      });
    }
  }

  // 🔹 Activar / Inactivar
  cambiarEstado(competencia: Competencia, estado: number): void {
    const id = Number(competencia?.id);
    if (!id) {
      Swal.fire("Error", "No se encontró el id de la competencia.", "error");
      return;
    }

    const accion = estado === 1 ? "activar" : "inactivar";

    Swal.fire({
      title: `¿Deseas ${accion} esta competencia?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Sí, ${accion}`,
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;

      const accionObservable =
        estado === 1
          ? this.competenciaService.activar(id)
          : this.competenciaService.inactivar(id);

      accionObservable.subscribe({
        next: () => {
          Swal.fire(
            "Éxito",
            `Competencia ${accion}da correctamente.`,
            "success",
          );
          this.cargarCompetencias();
        },
        error: (error: any) => {
          const mensaje =
            error?.error?.error || `No se pudo ${accion} la competencia.`;
          Swal.fire("Error", mensaje, "error");
        },
      });
    });
  }

    exportToPDF(tableId: string, nameFile: string, titleFile: string): void {
    Utils.exportToPDF(tableId, nameFile, titleFile);
  }

  exportarExcel(tableId: string, nameFile: string): void {
    Utils.exportVisibleTableToExcel(tableId, nameFile);
  }
}